import React, { useEffect, useState } from "react";

/**
 * Renders an <img> for a photo that may either be:
 *  - legacy inline: { data_url: "data:image/jpeg;base64,..." }
 *  - object-storage: { url: "/api/files/<id>", ... }  (requires auth)
 *
 * For object-storage URLs, we fetch as a blob using the stored bearer token so
 * we don't leak the token into the browser cache. Falls back to `?auth=<token>`
 * query-param when the parent needs a plain URL (e.g. thumbnails via CSS).
 */
export function photoSrcCandidate(photo) {
  if (!photo) return null;
  if (photo.data_url) return photo.data_url;
  if (photo.url) {
    const token = typeof window !== "undefined" ? window.localStorage.getItem("hos_token") : null;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const abs = photo.url.startsWith("http") ? photo.url : `${backend}${photo.url}`;
    return token ? `${abs}${abs.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}` : abs;
  }
  return null;
}

export default function AuthImage({ photo, className, alt = "", onClick, testId }) {
  const [src, setSrc] = useState(photo?.data_url || null);
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    async function run() {
      if (photo?.data_url) { setSrc(photo.data_url); return; }
      if (!photo?.url) { setSrc(null); return; }
      const token = window.localStorage.getItem("hos_token");
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const abs = photo.url.startsWith("http") ? photo.url : `${backend}${photo.url}`;
      try {
        const resp = await fetch(abs, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      } catch (_) {
        if (!cancelled) setSrc(null);
      }
    }
    run();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [photo?.data_url, photo?.url]);
  if (!src) {
    return (
      <div className={`bg-[color:var(--bg-surface-2)] flex items-center justify-center text-[10px] text-[color:var(--text-secondary)] ${className || ""}`}>
        loading…
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onClick={onClick} data-testid={testId}/>;
}
